import {
  CreateTaskAssignBody,
  GetFilterMetadataResponse,
  GetTaskAssignsResponse,
  GetTasksParams,
  GroupTaskAssignDetail,
  ImportAuditDetail,
  ImportAuditEntry,
  ImportAuditListResponse,
  ShowTaskAssignResponse,
  TaskAssignEditInfo,
} from '../types/taskAssigns';
import { client } from './index';

function appendMobileUserQueryParams(
  params: URLSearchParams,
  mobileUserIds: number[] | undefined,
  userId: number | undefined,
  useMobileUserIdsQueryParam: boolean | undefined,
) {
  if (useMobileUserIdsQueryParam && mobileUserIds?.length) {
    mobileUserIds.forEach((id) => params.append('mobile_user_ids[]', id.toString()));
    return;
  }
  if (userId != null) params.append('user_id', userId.toString());
}

const getTasks = async ({
  page = 1,
  taskAssignID,
  userId,
  mobileUserIds,
  useMobileUserIdsQueryParam,
  groupIds,
  locationId,
  locationIds,
  locationType,
  taskFormId,
  taskFormIds,
  states,
  state,
  startDate,
  endDate,
  sortBy,
  order,
  searchFormName,
  matchAssignId,
  assetId,
  assetTypeIds,
  origin,
}: GetTasksParams) => {
  const params = new URLSearchParams();

  if (page) params.append('page', page.toString());
  if (taskAssignID) params.append('assignID', taskAssignID);
  appendMobileUserQueryParams(params, mobileUserIds, userId, useMobileUserIdsQueryParam);
  if (groupIds?.length) {
    groupIds.forEach((id) => params.append('group_ids[]', id.toString()));
  }
  if (locationIds && locationIds.length > 0) {
    locationIds.forEach((id) => params.append('location_ids[]', id.toString()));
  } else if (locationId) {
    params.append('location_id', locationId.toString());
  }
  if (locationType) params.append('location_type', locationType);
  if (taskFormIds?.length) {
    taskFormIds.forEach((id) => params.append('task_form_ids[]', id.toString()));
  } else if (taskFormId) {
    params.append('task_form_id', taskFormId.toString());
  }
  if (states?.length) {
    states.forEach((value) => params.append('states[]', value));
  } else if (state) {
    params.append('state', state);
  }
  if (startDate) params.append('start', startDate);
  if (endDate) params.append('end', endDate);
  if (sortBy) params.append('sort_by', sortBy);
  if (order) params.append('order', order);
  if (searchFormName) params.append('search_form_name', searchFormName);
  if (matchAssignId) params.append('search_match_assign_id', 'true');
  if (assetId) params.append('asset_id', assetId);
  if (assetTypeIds) params.append('asset_type_ids', assetTypeIds);
  if (origin) params.append('origin', origin);

  const response = await client.get<GetTaskAssignsResponse>(
    `/task_assigns_react?${params.toString()}`,
  );
  const { data } = response;
  return data;
};

const getTasksBoxes = async ({
  page = 1,
  taskAssignID,
  userId,
  mobileUserIds,
  useMobileUserIdsQueryParam,
  groupIds,
  locationId,
  locationIds,
  locationType,
  taskFormId,
  taskFormIds,
  states,
  state,
  startDate,
  endDate,
  sortBy,
  order,
  searchFormName,
  assetId,
  assetTypeIds,
  origin,
}: GetTasksParams) => {
  const params = new URLSearchParams();

  if (page) params.append('page', page.toString());
  if (taskAssignID) params.append('assignID', taskAssignID);
  appendMobileUserQueryParams(params, mobileUserIds, userId, useMobileUserIdsQueryParam);
  if (groupIds?.length) {
    groupIds.forEach((id) => params.append('group_ids[]', id.toString()));
  }
  if (locationIds && locationIds.length > 0) {
    locationIds.forEach((id) => params.append('location_ids[]', id.toString()));
  } else if (locationId) {
    params.append('location_id', locationId.toString());
  }
  if (locationType) params.append('location_type', locationType);
  if (taskFormIds?.length) {
    taskFormIds.forEach((id) => params.append('task_form_ids[]', id.toString()));
  } else if (taskFormId) {
    params.append('task_form_id', taskFormId.toString());
  }
  if (states?.length) {
    states.forEach((value) => params.append('states[]', value));
  } else if (state) {
    params.append('state', state);
  }
  if (startDate) params.append('start', startDate);
  if (endDate) params.append('end', endDate);
  if (sortBy) params.append('sort_by', sortBy);
  if (order) params.append('order', order);
  if (searchFormName) params.append('search_form_name', searchFormName);
  if (assetId) params.append('asset_id', assetId);
  if (assetTypeIds) params.append('asset_type_ids', assetTypeIds);
  if (origin) params.append('origin', origin);

  const response = await client.get<GetTaskAssignsResponse>(
    `/task_assigns_react_boxes?${params.toString()}`,
  );
  const { data } = response;
  return data;
};

const getLightFilterMetadata = async (options?: { includeDeactivatedUsers?: boolean }) => {
  const params = new URLSearchParams();
  if (options?.includeDeactivatedUsers) {
    params.append('include_deactivated_users', 'true');
  }
  const query = params.toString();
  const url = query ? `/filter_metadata?${query}` : '/filter_metadata';
  const response = await client.get<GetFilterMetadataResponse>(url);
  const { data } = response;
  return data;
};

interface CreateTaskAssignResponse {
  task_assign_id?: number;
  assign_id?: string;
}

const createTaskAssign = async (taskAssign: CreateTaskAssignBody): Promise<CreateTaskAssignResponse> => {
  const response = await client.post<CreateTaskAssignResponse>('/task_assigns_create', taskAssign);
  return response.data ?? {};
};

const updateTaskAssign = async (taskId: number, taskAssign: CreateTaskAssignBody) => {
  const response = await client.put(`/task_assign_update/${taskId}`, taskAssign, {
    headers: {
      accept: 'application/json',
    },
  });
  const { data } = response;
  return data;
};

const notifyUsers = async (taskID: number) => {
  const response = await client.post('/tasks_notify_react.json', { task_id: taskID });
  const { data } = response;
  return data;
};

const deleteTask = async (taskID: number) => {
  const response = await client.delete(`/task_assigns_delete/${taskID}`);
  const { data } = response;
  return data;
};

// Soft-deletes the GroupTaskAssign and cascades to its non-completed
// children. Backend route: `DELETE /task_assigner/groups/:id`.
const deleteGroupTask = async (groupTaskAssignId: number) => {
  const response = await client.delete(`/task_assigner/groups/${groupTaskAssignId}`);
  const { data } = response;
  return data;
};

// Cancels the whole GroupTaskAssign (sets cancelled_at + reason and cancels
// pending children). Group analogue of the individual cancel; the individual
// endpoint 404s for a group row since its id is a GroupTaskAssign. Backend
// route: `POST /task_assigner/groups/:id/cancel`.
const cancelGroupTask = async (groupTaskAssignId: number, payload: { reason: string }) => {
  const response = await client.post(
    `/task_assigner/groups/${groupTaskAssignId}/cancel`,
    payload,
  );
  return response.data;
};

// Schedules a push notification to the pending members of the group task.
// Backend route: `POST /task_assigner/groups/:id/notify`. Optionally
// narrows the recipients to a subset of mobile_user_ids.
const notifyGroupTask = async (
  groupTaskAssignId: number,
  mobileUserIds?: number[],
) => {
  const payload = mobileUserIds?.length ? { mobile_user_ids: mobileUserIds } : {};
  const response = await client.post(`/task_assigner/groups/${groupTaskAssignId}/notify`, payload);
  const { data } = response;
  return data;
};

const getEditInfo = async (
  taskID: number,
  rowType?: string,
): Promise<TaskAssignEditInfo> => {
  // For a planning group row the id is a GroupTaskAssign; `row_type=group`
  // tells the backend to hydrate from it instead of a TaskAssign.
  const qs = rowType ? `?row_type=${encodeURIComponent(rowType)}` : '';
  const response = await client.get(`/task_assign_edit/${taskID}${qs}`);
  const { data } = response;
  return data;
};

type BulkDeleteTasksResponse = {
  result: string;
  // Optional to remain resilient against a deploy window where the rails side
  // has not yet shipped the new response shape, or an older client gets routed
  // to a newer rails revision.
  deleted_ids?: number[];
  skipped_ids?: number[];
};

// `selectedTaskIds` are individual TaskAssign ids. `groupTaskAssignIds` are
// group-row ids (a different table); they travel in their own param so the
// backend deletes the GroupTaskAssign (+ children) instead of treating the id
// as a TaskAssign that "doesn't exist / already has answers".
const bulkDeleteTasks = async (
  selectedTaskIds: number[],
  groupTaskAssignIds: number[] = [],
): Promise<BulkDeleteTasksResponse> => {
  const response = await client.post<BulkDeleteTasksResponse>('/tasks_bulk_delete_react', {
    form_ids: selectedTaskIds,
    group_task_assign_ids: groupTaskAssignIds,
  });
  return response.data;
};

const bulkNotifyUsers = async (selectedTaskIds: number[]) => {
  const response = await client.post('/bulk_notify_react', { form_ids: selectedTaskIds });
  const { data } = response;
  return data;
};

const updateTask = async (
  taskID: string,
  task: CreateTaskAssignBody['task_assign'],
  time: CreateTaskAssignBody['time'],
  todoValue: string,
) => {
  const taskAssign = {
    task_assign: task,
    time,
    'todo-value': todoValue,
  };
  const response = await client.put(`/task_assign_update/${taskID}`, taskAssign);
  const { data } = response;
  return data;
};

const updateTaskAssignAcceptance = async (taskID: number, acceptance: boolean) => {
  const response = await client.patch(`/task_assign_acceptance`, {
    accept: acceptance ? 'true' : 'false',
    task_assign_id: taskID,
  });
  const { data } = response;
  return data;
};

const getOldVersion = async () => {
  const response = await client.get('/old_task_assigner');
  const { data } = response;
  return data;
};

export interface locationInfo {
  value: string;
  label: string;
}

export const loadOptions = async (
  searchQuery: string,
  loadedOptions: locationInfo[],
  startId = 0,
) => {
  const params = new URLSearchParams();

  const lastId = loadedOptions.length > 0 ? loadedOptions[loadedOptions.length - 1].value : startId;
  params.append('start', lastId.toString());
  if (searchQuery != '') params.append('search', searchQuery);

  const request_url = `/filter_location?${params.toString()}`;
  const response = await client.get(request_url);

  const locationList = response.data.locations.map((entry: (string | number)[]) => ({
    value: entry[1].toString(),
    label: entry[0],
  }));

  return {
    options: locationList, // cada vez que se cumple hasMore, se agregan estos elementos
    hasMore: locationList.length >= 50,
  };
};

// Fetches the supervisor-facing detail for a single GroupTaskAssign.
// Backend route: `GET /task_assigner/groups/:id/detail`.
const getGroupTaskAssignDetail = async (
  groupTaskAssignId: number,
): Promise<GroupTaskAssignDetail> => {
  const response = await client.get<GroupTaskAssignDetail>(
    `/task_assigner/groups/${groupTaskAssignId}/detail`,
  );
  return response.data;
};

// Updates a GroupTaskAssign and cascades the change to children without
// `form_answer`. Children with a form_answer stay frozen on the snapshot
// they responded against. Backend route: `PUT /task_assigner/groups/:id`.
//
// Editable fields: `mandatory`, `gap`, `late_response_allowed`,
// `description`, `location_id/type`, `asset_id`, `start_time`, `form_id`.
// `group_id` is immutable after creation.
export interface UpdateGroupTaskAssignBody {
  group_task_assign: {
    form_id?: number;
    description?: string | null;
    gap?: number;
    location_id?: number | null;
    location_type?: 'Location' | 'NestableLocation' | null;
    start_time?: string;
    mandatory?: 'for_everyone' | 'for_nobody' | 'for_one';
    late_response_allowed?: boolean;
    asset_id?: number | null;
  };
  // Sent separately so the backend can cascade the new subtask set to
  // pending children without touching the parent's column list.
  to_do_checks?: string[];
  // Reminder template string. Same encoding the create flow uses
  // (`notif_type:n_periods:period_minutes` items joined by `-`). Empty
  // string asks the backend to wipe both the parent template and the
  // pending children's TaskNotification rows.
  notifications?: string;
}

const updateGroupTaskAssign = async (
  groupTaskAssignId: number,
  body: UpdateGroupTaskAssignBody,
) => {
  const response = await client.put<GroupTaskAssignDetail>(
    `/task_assigner/groups/${groupTaskAssignId}`,
    body,
  );
  return response.data;
};

export const getTaskAssign = async (taskAssignId: number): Promise<ShowTaskAssignResponse> => {
  const response = await client.get(`/task_assigns/${taskAssignId}`);
  const { data } = response;
  return data;
};

const importTasks = async (file: File) => {
  const formData = new FormData();
  formData.append('import[file]', file);

  const response = await client.post<{ message?: string; error?: string; audit_log_id?: number }>(
    '/import_tasks_react',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    },
  );
  return response.data;
};

const getImportAudits = async (page = 1): Promise<ImportAuditListResponse> => {
  const response = await client.get(`/task_assigns_react/import_audits?page=${page}`);
  return response.data;
};

const getImportAudit = async (id: number): Promise<ImportAuditDetail> => {
  const response = await client.get(`/task_assigns_react/import_audits/${id}`);
  return response.data;
};

const resolveImportAuditEntry = async (
  auditLogId: number,
  entryId: number,
  taskAssignId?: number,
): Promise<{ success: boolean; entry?: ImportAuditEntry }> => {
  const response = await client.post(
    `/task_assigns_react/import_audits/${auditLogId}/entries/${entryId}/resolve`,
    taskAssignId ? { task_assign_id: taskAssignId } : {},
  );
  return response.data;
};

export const taskAssignsApi = {
  getLightFilterMetadata,
  notifyUsers,
  deleteTask,
  getEditInfo,
  createTaskAssign,
  updateTaskAssign,
  getTasks,
  bulkDeleteTasks,
  bulkNotifyUsers,
  updateTask,
  getOldVersion,
  getTasksBoxes,
  loadOptions,
  updateTaskAssignAcceptance,
  getTaskAssign,
  getGroupTaskAssignDetail,
  updateGroupTaskAssign,
  deleteGroupTask,
  cancelGroupTask,
  notifyGroupTask,
  importTasks,
  getImportAudits,
  getImportAudit,
  resolveImportAuditEntry,
};
